import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton,
  Grid, Card, CardMedia, CardContent, CardActions, Alert, TextField,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip,
} from '@mui/material';
import { CloudUpload, Delete, Edit, Link, WarningAmber } from '@mui/icons-material';
import { mediaService } from '../../services/api';
import TopBar from '../../components/TopBar';

const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
const getImageUrl = (url) => url?.startsWith('http') ? url : `${MEDIA_BASE_URL}${url}`;

const MediaManagement = () => {
  const navigate = useNavigate();
  const [mediaItems, setMediaItems] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [title, setTitle] = useState('');
  const [hyperlink, setHyperlink] = useState('');
  const [viewerDiscretion, setViewerDiscretion] = useState(false);
  const [success, setSuccess] = useState('');

  // Edit dialog state
  const [editItem, setEditItem] = useState(null);
  const [editHyperlink, setEditHyperlink] = useState('');
  const [editViewerDiscretion, setEditViewerDiscretion] = useState(false);

  useEffect(() => { loadMedia(); }, []);

  const loadMedia = async () => {
    try {
      const response = await mediaService.getAllMedia();
      setMediaItems(response.data);
    } catch (err) {
      console.error('Failed to load media');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', title);
    formData.append('hyperlink', hyperlink);
    formData.append('viewerDiscretion', viewerDiscretion);
    try {
      await mediaService.uploadMedia(formData);
      setSuccess('Media uploaded successfully!');
      setSelectedFile(null);
      setTitle('');
      setHyperlink('');
      setViewerDiscretion(false);
      loadMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to upload');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this media?')) {
      try {
        await mediaService.deleteMedia(id);
        loadMedia();
      } catch (err) {
        console.error('Failed to delete');
      }
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditHyperlink(item.hyperlink || '');
    setEditViewerDiscretion(item.viewerDiscretion || false);
  };

  const handleEditSave = async () => {
    try {
      await mediaService.updateMedia(editItem.id, {
        hyperlink: editHyperlink,
        viewerDiscretion: editViewerDiscretion,
      });
      setSuccess('Media updated successfully!');
      setEditItem(null);
      loadMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Media Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        {/* Upload Form */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Upload New Media</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} accept="image/*" />
              <TextField
                size="small"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Hyperlink (optional)"
                placeholder="https://www.youtube.com/..."
                value={hyperlink}
                onChange={(e) => setHyperlink(e.target.value)}
                sx={{ minWidth: 320 }}
                InputProps={{ startAdornment: <Link sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={viewerDiscretion}
                    onChange={(e) => setViewerDiscretion(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarningAmber sx={{ fontSize: 18, color: '#ed6c02' }} />
                    <Typography variant="body2">Viewer Discretion</Typography>
                  </Box>
                }
              />
              <Button variant="contained" startIcon={<CloudUpload />} onClick={handleUpload}>Upload</Button>
            </Box>
          </Box>
        </Paper>

        {/* Media Grid */}
        <Grid container spacing={3}>
          {mediaItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  height="180"
                  image={getImageUrl(item.url)}
                  alt={item.title}
                  sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                />
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {item.viewerDiscretion && (
                      <Chip icon={<WarningAmber />} label="Viewer Discretion" size="small" color="warning" variant="outlined" />
                    )}
                    {item.hyperlink && (
                      <Chip
                        icon={<Link />}
                        label="Has Link"
                        size="small"
                        color="primary"
                        variant="outlined"
                        component="a"
                        href={item.hyperlink}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ pt: 0 }}>
                  <Button size="small" startIcon={<Edit />} onClick={() => openEdit(item)}>Edit</Button>
                  <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(item.id)}>Delete</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Media — {editItem?.title}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Hyperlink (optional)"
            placeholder="https://www.youtube.com/..."
            value={editHyperlink}
            onChange={(e) => setEditHyperlink(e.target.value)}
            fullWidth
            InputProps={{ startAdornment: <Link sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={editViewerDiscretion}
                onChange={(e) => setEditViewerDiscretion(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmber sx={{ fontSize: 18, color: '#ed6c02' }} />
                <Typography variant="body2">Viewer Discretion — show warning for 5 seconds before this image</Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MediaManagement;
